import {
  supabase,
  Sentry,
  isDev,
  SupabaseError,
  withRetry,
  shouldRethrow,
  describeSupabaseFailure,
} from './shared'
import {
  isClientNetworkFailure,
  isDownloadRateLimited,
  isStatementTimeout,
  shouldReportTransientOnce,
} from './expected-failure'
import { downloadAccountPath, downloadAccountTarget } from '../download-accounting'
import type { Sound, Category } from '../supabase-types'
import { normalizeAudioSourceUrl } from '../audio-url-normalization'
import { pickDiverseSounds } from '../featured-sounds'
import { stripFillerWords } from '../search-filler-words'
import { findSearchRescue, type SearchRescue, type SuggestionRow } from '../search-rescue'
import { EXCLUDE_MATURE_FILTER, MATURE_TAG, withoutMature } from '../mature-content'
import { withoutRetiredCategories } from '../category-taxonomy'

// Column set for list pages. waveform_data rides along: every active row stores
// exactly 40 peaks (~150 bytes of JSON), and a row without it used to fetch and
// decode its whole WAV in the browser just to draw the waveform — a full
// storage download per visible row, billed as Supabase egress. preview_audio_path
// is what playback reads (see playbackAudioSource in lib/sound-audio-source.ts).
export const LIST_COLUMNS = 'id,slug,title,description,category,duration,size,format,downloads,featured,file_url,preview_audio_path,waveform_data,created_at,updated_at,tesla_filename,volume_db,original_volume_db,is_normalized,tags,is_premium,preview_url,is_active,creator_name,source_reference,likes,dislikes,compatible_models'

function normalizeSoundAudioUrls<T extends { file_url?: unknown; preview_url?: unknown }>(sound: T): T {
  if (!sound) {
    return sound
  }

  const normalized = { ...sound }
  let changed = false

  if (typeof sound.file_url === 'string') {
    const fileUrl = normalizeAudioSourceUrl(sound.file_url)
    if (fileUrl !== sound.file_url) {
      normalized.file_url = fileUrl
      changed = true
    }
  }

  if (typeof sound.preview_url === 'string') {
    const previewUrl = normalizeAudioSourceUrl(sound.preview_url)
    if (previewUrl !== sound.preview_url) {
      normalized.preview_url = previewUrl
      changed = true
    }
  }

  if (!changed) {
    return sound
  }

  return normalized
}

function normalizeSoundList<T extends { file_url?: unknown; preview_url?: unknown }>(sounds: T[] | null | undefined): T[] {
  return (sounds || []).map(normalizeSoundAudioUrls)
}

/**
 * The PostgREST `or=` string a curated collection's search terms resolve to:
 * each term matched as a literal substring against title OR description.
 *
 * Extracted so the collection PAGE and any COUNT of that collection are
 * answering the same question — a listing that says "45 sounds" over a page
 * showing 82 is a fabricated figure by drift rather than by intent.
 *
 * LIKE metacharacters and PostgREST filter-string delimiters are escaped so
 * a term is never interpreted as a pattern or as extra filter syntax.
 */
export function curatedSearchTermFilter(searchTerms: readonly string[]): string {
  return searchTerms
    .flatMap(term => {
      const safe = term.replace(/[\\%_,()]/g, '\\$&')
      return [`title.ilike.%${safe}%`, `description.ilike.%${safe}%`]
    })
    .join(',')
}

type SupabaseClient = NonNullable<typeof supabase>

/** PostgREST answers PGRST202 when no function matches the named arguments sent. */
const RPC_SIGNATURE_NOT_FOUND = 'PGRST202'

/**
 * Calls a search or count RPC with `p_exclude_mature` so the database leaves
 * flagged rows out of the rows and the count together (migration
 * 20261002_mature_flag_search_counts.sql). Until that migration is applied the
 * function has no such parameter, so a PGRST202 retries the original call and
 * the caller's own filtering takes over.
 */
async function rpcExcludingMature(
  db: SupabaseClient,
  fn: string,
  args: Record<string, unknown>,
  includeMature: boolean | undefined,
) {
  if (includeMature) return db.rpc(fn, args)
  const result = await db.rpc(fn, { ...args, p_exclude_mature: true })
  if (result.error?.code === RPC_SIGNATURE_NOT_FOUND) return db.rpc(fn, args)
  return result
}

interface TrendingFilters {
  category?: string | string[]
  compatibleModels?: string
  premiumOnly?: boolean
  includeMature?: boolean
}

/** The filter methods the trending queries use, each returning the builder. */
interface TrendingFilterable<Self> {
  eq(column: string, value: unknown): Self
  in(column: string, values: unknown[]): Self
  overlaps(column: string, values: unknown[]): Self
  or(filters: string, options: { referencedTable: string }): Self
}

/**
 * The shared WHERE for the Trending sort without a search term: sounds with
 * downloads in the last seven days (the `trending_sounds` rollup), joined to
 * the live catalogue. PostgREST cannot order a parent by an embedded column,
 * so the query starts at the rollup and embeds the sound.
 */
function applyTrendingFilters<Query extends TrendingFilterable<Query>>(
  query: Query,
  options: TrendingFilters,
): Query {
  let next = query.eq('sounds.is_active', true)
  if (!options.includeMature) next = next.or(EXCLUDE_MATURE_FILTER, { referencedTable: 'sounds' })
  if (options.category && options.category !== 'All') {
    next = Array.isArray(options.category)
      ? next.in('sounds.category', options.category)
      : next.eq('sounds.category', options.category)
  }
  if (options.compatibleModels) next = next.overlaps('sounds.compatible_models', [options.compatibleModels])
  if (options.premiumOnly) next = next.eq('sounds.is_premium', true)
  return next
}

// Enhanced database operations
export const soundsApi = {
  /**
   * Fetch sounds with filtering, sorting, and pagination.
   *
   * Sort options (all secondary-ordered by `id` for stable pagination):
   * - `downloads` desc — "Most Downloaded" (default UI sort, highest download count first)
   * - `created_at` desc — "Newest" (recent uploads first)
   * - `created_at` asc — "Oldest"
   * - `title` asc/desc — alphabetical
   * - `duration` asc/desc — shortest / longest
   * - `volume_db` asc/desc — quietest / loudest
   *
   * When `search` is provided, sorts are applied server-side via the
   * `search_sounds_ranked` RPC; otherwise applied via Supabase order chains.
   * Defaults: sortBy = 'created_at', sortOrder = 'desc'.
   */
  async getSounds(options: {
    /**
     * Single DB category name, or an array for umbrella pages whose sounds
     * span several fine-grained DB categories (e.g. /sounds/category/games →
     * Modern Gaming + Retro Gaming + Mario & Nintendo + …). Arrays are only
     * honored on the direct-query path; the ranked-search RPC takes a single
     * category, so array filters are dropped there (search UI never passes one).
     */
    category?: string | string[]
    compatibility?: string
    compatibleModels?: string   // Tesla model name: 'Model 3', 'Model Y', 'Model S', 'Model X', 'Cybertruck'
    volumeRange?: [number, number]
    tags?: string[]
    sortBy?: 'created_at' | 'downloads' | 'title' | 'duration' | 'volume_db' | 'trending'
    sortOrder?: 'asc' | 'desc'
    limit?: number
    offset?: number
    search?: string
    durationRange?: 'all' | '0-2' | '2-5' | '5+'
    fileSizeRange?: 'all' | '<500' | '500-1000' | '1000+'
    teslaCompatible?: boolean
    premiumOnly?: boolean
    columns?: string  // Supabase select string; defaults to '*'. Use LIST_COLUMNS to exclude heavy columns.
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
    /** Rows tagged `mature` are left out unless a caller opts in (X7 / CAT-4). */
    includeMature?: boolean
  } = {}): Promise<Sound[]> {
    const debugLog = (message: string, data?: any) => {
      if (process.env.NODE_ENV === 'development') {
        const timestamp = new Date().toLocaleTimeString()
        if (data !== undefined) {
          console.log(`[${timestamp}] [soundsApi.getSounds] ${message}:`, data)
        } else {
          console.log(`[${timestamp}] [soundsApi.getSounds] ${message}`)
        }
      }
    }

    debugLog('Called with options', options)

    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // If search is provided, use the ranked full-text search RPC
        // This searches title (high weight), description (lower weight), and tags (lowest weight)
        if (options.search && options.search.trim()) {
          const searchTerm = options.search.trim().replace(/[<>]/g, '')
          debugLog('Using FTS ranked search', searchTerm)

          // Calculate duration range values for RPC
          let durationMin: number | null = null
          let durationMax: number | null = null
          if (options.durationRange === '0-2') {
            durationMin = 0
            durationMax = 2
          } else if (options.durationRange === '2-5') {
            durationMin = 2
            durationMax = 5
          } else if (options.durationRange === '5+') {
            durationMin = 5
          }

          // Bound once so this closure keeps the null-narrowing from the guard
          // at the top of the retry body.
          const db = supabase
          const runRankedSearch = (term: string) =>
            rpcExcludingMature(db, 'search_sounds_ranked', {
              search_query: term,
              category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
              duration_min: durationMin,
              duration_max: durationMax,
              tesla_compatible: options.teslaCompatible || false,
              sort_by: options.sortBy || 'rank',
              sort_order: options.sortOrder || 'desc',
              result_limit: options.limit || 50,
              result_offset: options.offset || 0,
              p_model_filter: options.compatibleModels || null
            }, options.includeMature)

          let { data, error } = await runRankedSearch(searchTerm)

          // Filler-word fallback (TES-2229). Full text needs every lexeme in the
          // query to be present, so `hee hee sound effect` fails for the same
          // reason the ILIKE route's contiguous-substring match does: "sound"
          // and "effect" are not in the title. This is the surface the issue is
          // named after — /sounds drives `search_sounds_ranked` straight from
          // the browser, it does not go through /api/sounds — so the fallback
          // has to exist in both backends or the library search keeps handing
          // Google visitors an empty page.
          //
          // Gated on a first page that came back empty: `offset` guards against
          // reinterpreting a real page-N-past-the-end as a failed search, and
          // an empty page 1 is the only state a visitor ever reads as "you have
          // nothing". `stripFillerWords` returns null when nothing was stripped
          // or when everything was, so a meaningless query stays empty.
          if (!error && (data as Sound[] | null)?.length === 0 && !options.offset) {
            const strippedTerm = stripFillerWords(searchTerm)
            if (strippedTerm) {
              debugLog('Empty FTS result, retrying without filler words', strippedTerm)
              const retried = await runRankedSearch(strippedTerm)
              if (!retried.error && (retried.data as Sound[] | null)?.length) {
                data = retried.data
              }
            }
          }

          if (error) {
            debugLog('RPC search error', error)
            throw new SupabaseError(`Failed to search sounds: ${error.message}`, error)
          }

          debugLog('RPC search successful', { recordCount: data?.length || 0 })
          // The database already left flagged rows out; this covers the window
          // before the mature-flag migration is applied.
          const searchResults = options.includeMature ? (data as Sound[]) : withoutMature((data as Sound[]) ?? [])
          if (options.premiumOnly) {
            return normalizeSoundList(searchResults?.filter(s => s.is_premium) ?? [])
          }
          return normalizeSoundList(searchResults)
        }

        // Trending without a search term lists the sounds downloaded this week,
        // most velocity first. With a search term the ranked RPC above orders by
        // the same score (sort_by = 'trending').
        if (options.sortBy === 'trending') {
          const limit = options.limit || 50
          const offset = options.offset || 0
          const columns = options.columns || '*'
          const trendingQuery = supabase
            .from('trending_sounds')
            .select(`score, sounds!inner(${columns})`)
          const { data, error } = await applyTrendingFilters(trendingQuery, options)
            .order('score', { ascending: false })
            .order('sound_id', { ascending: true })
            .range(offset, offset + limit - 1)
            .overrideTypes<Array<{ sounds: Sound | Sound[] | null }>, { merge: false }>()
          if (error) throw new SupabaseError(`Failed to fetch trending sounds: ${error.message}`, error)
          const trending = (data ?? []).flatMap(row => {
            const sound = Array.isArray(row.sounds) ? row.sounds[0] : row.sounds
            return sound?.id ? [normalizeSoundAudioUrls(sound)] : []
          })
          return trending
        }

        // Non-search query: use standard query builder
        debugLog('Building query...')
        const selectColumns = options.columns || '*'
        let query = supabase
          .from('sounds')
          .select(selectColumns)
          .eq('is_active', true)

        if (!options.includeMature) {
          query = query.or(EXCLUDE_MATURE_FILTER)
        }

        // Apply filters
        if (options.category && options.category !== 'All') {
          debugLog('Adding category filter', options.category)
          query = Array.isArray(options.category)
            ? query.in('category', options.category)
            : query.eq('category', options.category)
        }

        // Duration range filter
        if (options.durationRange && options.durationRange !== 'all') {
          debugLog('Adding duration range filter', options.durationRange)
          if (options.durationRange === '0-2') {
            query = query.gte('duration', 0).lte('duration', 2)
          } else if (options.durationRange === '2-5') {
            query = query.gt('duration', 2).lte('duration', 5)
          } else if (options.durationRange === '5+') {
            query = query.gt('duration', 5)
          }
        }

        // Tesla compatibility filter (duration <= 5s for Tesla lock sounds)
        if (options.teslaCompatible) {
          debugLog('Adding Tesla compatibility filter')
          query = query.lte('duration', 5)
        }

        // Note: fileSizeRange filtering is done client-side because 'size' is stored as string
        // This is acceptable since pagination reduces the dataset significantly

        if (options.volumeRange) {
          debugLog('Adding volume range filter', options.volumeRange)
          query = query
            .gte('volume_db', options.volumeRange[0])
            .lte('volume_db', options.volumeRange[1])
        }

        if (options.tags && options.tags.length > 0) {
          debugLog('Adding tags filter', options.tags)
          query = query.overlaps('tags', options.tags)
        }

        if (options.compatibleModels) {
          debugLog('Adding compatible_models filter', options.compatibleModels)
          query = query.overlaps('compatible_models', [options.compatibleModels])
        }

        if (options.premiumOnly) {
          debugLog('Adding is_premium filter')
          query = query.eq('is_premium', true)
        }

        // Apply sorting
        const sortBy = options.sortBy || 'created_at'
        const sortOrder = options.sortOrder || 'desc'
        debugLog('Adding sorting', { sortBy, sortOrder })

        if (sortBy === 'title') {
          query = query
            .order('title', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'downloads') {
          query = query
            .order('downloads', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'duration') {
          query = query
            .order('duration', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'volume_db') {
          query = query
            .order('volume_db', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else {
          query = query
            .order('created_at', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        }

        // Apply pagination (offset + limit) or just limit
        if (options.offset !== undefined && options.offset > 0) {
          const limit = options.limit || 50  // Default limit when using offset
          debugLog('Adding pagination', { offset: options.offset, limit })
          query = query.range(options.offset, options.offset + limit - 1)
        } else if (options.limit) {
          debugLog('Adding limit', options.limit)
          query = query.limit(options.limit)
        }

        debugLog('Executing query...')
        const { data, error } = await query

        if (error) {
          debugLog('Query error', error)
          throw new SupabaseError(`Failed to fetch sounds: ${error.message}`, error)
        }

        debugLog('Query successful', { recordCount: data?.length || 0 })

        // Validate each sound object
        const validatedSounds = ((data as unknown as Sound[]) || []).map((sound, index) => {
          const validationErrors: string[] = []

          if (!sound.id || typeof sound.id !== 'string') {
            validationErrors.push('Invalid or missing id')
          }
          if (!sound.title || typeof sound.title !== 'string') {
            validationErrors.push('Invalid or missing title')
          }
          if (!sound.file_url || typeof sound.file_url !== 'string') {
            validationErrors.push('Invalid or missing file_url')
          }

          if (validationErrors.length > 0) {
            if (isDev) console.log(`[soundsApi.getSounds] Sound validation failed at index ${index}:`, {
              sound,
              errors: validationErrors
            })
            debugLog(`Validation errors for sound ${index}`, validationErrors)
          }

          return normalizeSoundAudioUrls(sound)
        })

        debugLog('Validation complete', {
          totalSounds: validatedSounds.length,
          sampleSound: validatedSounds[0] ? {
            id: validatedSounds[0].id,
            title: validatedSounds[0].title,
            file_url: validatedSounds[0].file_url
          } : 'No sounds'
        })

        return validatedSounds
      })
    } catch (error) {
      if (!isStatementTimeout(error) || shouldReportTransientOnce('getSounds', error)) {
        Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSounds' } })
      }
      if (isDev) debugLog('Error occurred', error)
      // ISR callers pass throwOnError: true so a transient failure rejects the
      // page's Promise.all and Next.js keeps the last good cached render rather
      // than caching an empty list (ISR poisoning). Client-side callers leave
      // throwOnError unset (falsy) and get the original [] fallback.
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  // Get total count of sounds matching filters (efficient count query)
  async getSoundsCount(options: {
    category?: string
    search?: string
    /** Only 'trending' changes the count: it lists this week's downloaded sounds. */
    sortBy?: string
    durationRange?: 'all' | '0-2' | '2-5' | '5+'
    teslaCompatible?: boolean
    compatibleModels?: string
    premiumOnly?: boolean
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning 0. */
    throwOnError?: boolean
    /** Counts rows tagged `mature` too. Leave unset to match what getSounds lists. */
    includeMature?: boolean
  } = {}): Promise<number> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // If search is provided, use the FTS count RPC
      if (options.search && options.search.trim()) {
        const searchTerm = options.search.trim().replace(/[<>]/g, '')

        // Calculate duration range values for RPC
        let durationMin: number | null = null
        let durationMax: number | null = null
        if (options.durationRange === '0-2') {
          durationMin = 0
          durationMax = 2
        } else if (options.durationRange === '2-5') {
          durationMin = 2
          durationMax = 5
        } else if (options.durationRange === '5+') {
          durationMin = 5
        }

        // Bound once so this closure keeps the null-narrowing from the guard above.
        const db = supabase
        const runCount = (term: string) =>
          rpcExcludingMature(db, 'search_sounds_count', {
            search_query: term,
            category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
            duration_min: durationMin,
            duration_max: durationMax,
            tesla_compatible: options.teslaCompatible || false,
            p_model_filter: options.compatibleModels || null
          }, options.includeMature)

        const { data, error } = await runCount(searchTerm)

        if (error) {
          throw new SupabaseError(`Failed to get sounds count: ${error.message}`, error)
        }

        // The count half of the TES-2229 fallback. It has to use the same gate
        // and the same stripped term as the list half in `getSounds`, or the
        // library reports "0 sounds" over a populated grid.
        if (((data as number) || 0) === 0) {
          const strippedTerm = stripFillerWords(searchTerm)
          if (strippedTerm) {
            const retried = await runCount(strippedTerm)
            if (!retried.error) return (retried.data as number) || 0
          }
        }

        return (data as number) || 0
      }

      if (options.sortBy === 'trending') {
        const countQuery = supabase
          .from('trending_sounds')
          .select('sound_id, sounds!inner(id)', { count: 'exact', head: true })
        const { count, error } = await applyTrendingFilters(countQuery, options)
        if (error) throw new SupabaseError(`Failed to get trending count: ${error.message}`, error)
        return count || 0
      }

      // Non-search count: use standard query
      let query = supabase
        .from('sounds')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true)

      if (!options.includeMature) {
        query = query.or(EXCLUDE_MATURE_FILTER)
      }

      // Apply filters (same as getSounds)
      if (options.category && options.category !== 'All') {
        query = query.eq('category', options.category)
      }

      if (options.durationRange && options.durationRange !== 'all') {
        if (options.durationRange === '0-2') {
          query = query.gte('duration', 0).lte('duration', 2)
        } else if (options.durationRange === '2-5') {
          query = query.gt('duration', 2).lte('duration', 5)
        } else if (options.durationRange === '5+') {
          query = query.gt('duration', 5)
        }
      }

      if (options.teslaCompatible) {
        query = query.lte('duration', 5)
      }

      if (options.compatibleModels) {
        query = query.overlaps('compatible_models', [options.compatibleModels])
      }

      if (options.premiumOnly) {
        query = query.eq('is_premium', true)
      }

      const { count, error } = await query

      if (error) {
        throw new SupabaseError(`Failed to get sounds count: ${error.message}`, error)
      }

      return count || 0
    } catch (error) {
      if (!isClientNetworkFailure(error)) {
        Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsCount' } })
      }
      if (shouldRethrow(options.throwOnError)) throw error
      return 0
    }
  },

  /**
   * What to show for a search that found nothing (X6 / LIB-05): the query with
   * misspelled words corrected, or with unmatched words left out, whichever
   * first finds sounds under the same filters. Null when nothing does, and
   * also when `suggest_search_terms` is not installed yet, so a missing
   * migration leaves the empty state exactly as it was.
   */
  async getSearchRescue(options: {
    search: string
    category?: string
    compatibleModels?: string
    premiumOnly?: boolean
  }): Promise<SearchRescue | null> {
    const db = supabase
    if (!db) return null
    return findSearchRescue(options.search, {
      suggest: async query => {
        const { data, error } = await db.rpc('suggest_search_terms', { p_query: query })
        if (error) {
          if (error.code === RPC_SIGNATURE_NOT_FOUND) return []
          throw new SupabaseError(`Failed to get search suggestions: ${error.message}`, error)
        }
        return (data ?? []) as SuggestionRow[]
      },
      count: term =>
        this.getSoundsCount({
          search: term,
          category: options.category,
          compatibleModels: options.compatibleModels,
          premiumOnly: options.premiumOnly,
          throwOnError: true,
        }),
    })
  },

  // Get sounds by category (legacy method)
  async getSoundsByCategory(category: string, options: { throwOnError?: boolean } = {}): Promise<Sound[]> {
    return this.getSounds({ category, throwOnError: options.throwOnError })
  },

  // Get single sound
  async getSound(id: string, options: { throwOnError?: boolean } = {}): Promise<Sound | null> {
    Sentry.addBreadcrumb({
      category: 'soundsApi.getSound',
      message: 'getSound:start',
      level: 'info',
      data: {
        id,
        idLength: id?.length,
        looksLikeUuid: typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
        runtime: typeof process !== 'undefined' ? process.env.NEXT_RUNTIME ?? 'node' : 'unknown',
        supabaseInitialized: Boolean(supabase),
      },
    })
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            Sentry.addBreadcrumb({
              category: 'soundsApi.getSound',
              message: 'getSound:not-found',
              level: 'info',
              data: { id, code: error.code },
            })
            return null
          }
          throw new SupabaseError(`Failed to fetch sound: ${error.message}`, error)
        }

        return normalizeSoundAudioUrls(data as Sound)
      })
    } catch (error) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string; status?: number }
      Sentry.captureException(error, {
        tags: { context: 'soundsApi', action: 'getSoundById' },
        extra: {
          id,
          errorMessage: err?.message,
          errorCode: err?.code,
          errorDetails: err?.details,
          errorHint: err?.hint,
          errorStatus: err?.status,
          runtime: typeof process !== 'undefined' ? process.env.NEXT_RUNTIME ?? 'node' : 'unknown',
        },
      })
      // ISR callers (app/sounds/[id]) pass throwOnError: true so a transient
      // failure rejects the render and Next keeps the last good snapshot,
      // instead of a null that becomes notFound() and is cached as a 404 for
      // the whole revalidate window. A real not-found (PGRST116) still
      // returns null above.
      if (shouldRethrow(options.throwOnError)) throw error
      return null
    }
  },

  // Batch fetch by IDs (e.g. saved-sounds hydration). Returns only found rows,
  // in no guaranteed order — callers re-order. Caps at 100 ids defensively.
  async getSoundsByIds(ids: string[], options: { throwOnError?: boolean } = {}): Promise<Sound[]> {
    if (ids.length === 0) return []
    const capped = ids.slice(0, 100)
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized')
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .in('id', capped)

        if (error) {
          throw new SupabaseError(`Failed to batch fetch sounds: ${error.message}`, error)
        }

        return (data as Sound[]).map(normalizeSoundAudioUrls)
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'soundsApi', action: 'getSoundsByIds' },
        extra: {
          idCount: capped.length,
        },
      })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  async getSoundBySlug(slug: string, options: { throwOnError?: boolean } = {}): Promise<Sound | null> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            return null
          }
          throw new SupabaseError(`Failed to fetch sound by slug: ${error.message}`, error)
        }

        return normalizeSoundAudioUrls(data as Sound)
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundBySlug' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return null
    }
  },

  async getSoundByShortId(shortId: string): Promise<string | null> {
    if (!/^[0-9a-f]+$/i.test(shortId)) return null
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('id')
          .like('id', `${shortId}-%`)
          .limit(1)

        if (error) {
          throw new SupabaseError(`Failed to lookup short ID: ${error.message}`, error)
        }

        if (!data || data.length === 0) {
          return null
        }

        return data[0].id as string
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundByShortId' } })
      return null
    }
  },

  /**
   * Which of these slugs belong to a mature-flagged sound. Editorial pages pin
   * sounds by slug or id without going through a list fetcher, so they ask this
   * before linking. A failed lookup means the flag state is unknown: ISR callers
   * pass throwOnError so the page keeps its last good render, others get every
   * slug back as flagged so nothing unverified is linked.
   */
  async getFlaggedSlugs(slugs: string[], options: { throwOnError?: boolean } = {}): Promise<Set<string>> {
    if (slugs.length === 0) return new Set()
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized')
        }
        const { data, error } = await supabase
          .from('sounds')
          .select('slug')
          .in('slug', slugs)
          .contains('tags', [MATURE_TAG])
        if (error) {
          throw new SupabaseError(`Failed to fetch flagged slugs: ${error.message}`, error)
        }
        return new Set(((data as { slug: string }[] | null) ?? []).map(row => row.slug))
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getFlaggedSlugs' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return new Set(slugs)
    }
  },

  // Get featured sounds with category diversity for hero carousel
  async getFeaturedSounds(limit: number = 8): Promise<Sound[]> {
    try {
      // Fetch extra sounds to allow for category diversity and randomization
      const sounds = await this.getSounds({ sortBy: 'downloads', sortOrder: 'desc', limit: limit * 2 })

      // Category-diverse selection — shared with the client TrendingSection
      // (lib/featured-sounds.ts) so server-rendered and hydrated rails match.
      return pickDiverseSounds(sounds, limit)
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getFeaturedSounds' } })
      return []
    }
  },

  // Get sounds by Tesla model compatibility. `compatibleModels` is the option
  // the query builder actually applies — the old `compatibility` key was never
  // read, so this returned the whole active catalog with '*' (~10 MB) for a
  // caller that renders 24 rows, and the model/year pages timed out at build.
  async getSoundsByTeslaModel(
    modelName: string,
    options: { limit?: number; throwOnError?: boolean } = {},
  ): Promise<Sound[]> {
    return this.getSounds({
      compatibleModels: modelName,
      limit: options.limit,
      sortBy: 'downloads',
      sortOrder: 'desc',
      columns: LIST_COLUMNS,
      throwOnError: options.throwOnError,
    })
  },

  // Get sounds by collection (database-based)
  async getSoundsByCollection(collectionId: string): Promise<Sound[]> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('collection_items')
          .select('sounds!inner(*)')
          .eq('collection_id', collectionId)
          .eq('sounds.is_active', true)
          .or(EXCLUDE_MATURE_FILTER, { referencedTable: 'sounds' });

        if (error) {
          throw new SupabaseError(`Failed to fetch sounds for collection: ${error.message}`, error);
        }

        return normalizeSoundList(data?.flatMap((item: any) => item.sounds ? [item.sounds] : []) as Sound[] || []);
      });
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsByCollection' } })
      return [];
    }
  },

  // Get sounds by curated collection (search term based)
  async getSoundsByCuratedCollection(collectionId: string, options?: { limit?: number; throwOnError?: boolean }): Promise<Sound[]> {
    // Import dynamically to avoid circular dependency
    const { getCuratedCollection } = await import('../curated-collections')
    const collection = getCuratedCollection(collectionId)

    if (!collection) {
      return []
    }

    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const orFilters = curatedSearchTermFilter(collection.searchTerms)

        const { data, error } = await supabase
          .from('sounds')
          // LIST_COLUMNS (not '*') keeps the heavy columns (embedding,
          // search_vector, transcript) out of the up-to-200 rows serialized
          // into the client CollectionSortGrid (TES-834).
          .select(LIST_COLUMNS)
          // Deactivated rows are merged duplicates: /sounds/[id] either 308s them
          // to a live replacement or serves them noindex (see app/sounds/[id]/page.tsx).
          // Without this filter every curated collection rendered them as normal
          // cards — on kids-family that was 36 of 82 matches (44%) pointing at
          // redirect/noindex URLs from a page whose whole job is to rank.
          .eq('is_active', true)
          .or(EXCLUDE_MATURE_FILTER)
          .or(orFilters)
          .order('downloads', { ascending: false })
          .limit(options?.limit || 50)

        if (error) {
          throw new SupabaseError(`Failed to fetch sounds for curated collection: ${error.message}`, error)
        }

        return normalizeSoundList(data as Sound[])
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsByCuratedCollection' } })
      if (shouldRethrow(options?.throwOnError)) throw error
      return []
    }
  },

  /**
   * How many ACTIVE sounds each curated collection actually holds, keyed by id.
   *
   * Counts the same sounds `getSoundsByCuratedCollection` lists, so the number
   * a listing shows can never drift from the page it links to.
   * `curatedCollections[].estimatedCount` is an estimate and must never reach a
   * user-facing surface (docs/ANTI_SLOP.md).
   *
   * ## Why this is one RPC and not one query per collection (TES-2142)
   *
   * This used to ask PostgREST for `count=exact` under `curatedSearchTermFilter`'s
   * `or=`, once per collection. Sci-Fi Classics is 43 terms, so that is 86
   * unanchored `ILIKE` predicates over every row — 284 ms of pure CPU, and the
   * planner will not use a trgm index for an OR that wide (it takes the index
   * at ~20 predicates and abandons it at 86). Thirteen of those in parallel per
   * homepage render hit the DB statement timeout at ~10 concurrent renders.
   *
   * `count_curated_collections` runs one nested loop per term instead, so each
   * inner scan sees a single `ILIKE` pair and takes the trgm index whatever the
   * term count — and answers for all thirteen collections in one round trip.
   * Measured on prod: 10 concurrent renders went from p100 10.7 s with ten
   * `57014`s to p100 532 ms with none.
   *
   * The search terms are passed in rather than stored in the database, so
   * `lib/curated-collections.ts` stays the only place a collection is defined.
   *
   * An id missing from the result has no established count, and the caller
   * renders no count at all for it — "0 sounds" would be a claim this doesn't
   * know to be true. Returns {} when there is nothing to ask (no Supabase
   * client at build time, no recognised id) or on the expected statement
   * timeout. Any other failure REJECTS, so the one caller
   * (`getDisplayCollectionCounts`) reports it once for the whole batch.
   */
  async getCuratedCollectionCounts(
    collectionIds: string[],
    options: { includeMature?: boolean } = {},
  ): Promise<Record<string, number>> {
    if (collectionIds.length === 0 || !supabase) return {}

    const { getCuratedCollection } = await import('../curated-collections')
    const collections = collectionIds.flatMap(id => {
      const collection = getCuratedCollection(id)
      return collection ? [{ slug: id, terms: collection.searchTerms }] : []
    })
    if (collections.length === 0) return {}

    try {
      const { data, error, status, statusText } = await rpcExcludingMature(
        supabase,
        'count_curated_collections',
        { collections },
        options.includeMature,
      )

      if (error) {
        throw new SupabaseError(
          `Failed to count curated collections: ${describeSupabaseFailure(error, status, statusText)}`,
          error,
        )
      }

      const counts: Record<string, number> = {}
      for (const row of (data ?? []) as { slug: string; sound_count: number }[]) {
        if (typeof row?.sound_count === 'number') counts[row.slug] = row.sound_count
      }
      return counts
    } catch (error) {
      // Still the expected failure under enough load, just far further out —
      // the caller renders countless rows, which is the whole degrade path.
      if (isStatementTimeout(error)) return {}
      throw error
    }
  },

  // Get sounds by volume range
  async getSoundsByVolumeRange(min: number, max: number): Promise<Sound[]> {
    return this.getSounds({ volumeRange: [min, max] })
  },

  async incrementDownloads(id: string): Promise<void> {
    return soundsApi.accountDownload({ soundId: id })
  },

  async accountDownload(options: { soundId?: string | null } = {}): Promise<void> {
    const path = downloadAccountPath(downloadAccountTarget(options.soundId))
    try {
      const res = await fetch(path, { method: 'POST' })
      if (res.status === 429) return
      if (!res.ok && res.status !== 204) {
        throw new Error(`Download increment failed: ${res.status}`)
      }
    } catch (error) {
      if (isDownloadRateLimited(error) || isClientNetworkFailure(error)) return
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'incrementDownloads' } })
    }
  },

  // Increment like count
  async incrementLikes(_id: string): Promise<void> {
    // NOTE: likes field doesn't exist in database - this is a no-op
    // Will need to add likes field to database schema if we want to enable this
  },

  // Get categories
  async getCategories(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<Category[]> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name') // Sort by name instead of non-existent sort_order

        if (error) {
          throw new SupabaseError(`Failed to fetch categories: ${error.message}`, error)
        }

        return withoutRetiredCategories((data as Category[]) || [])
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getCategories' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  async getCategoryCounts(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning {}. */
    throwOnError?: boolean
  } = {}): Promise<Record<string, number>> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // Aggregated in Postgres (supabase/migrations/20260920_active_category_counts.sql)
        // rather than folded here. A `select('category').eq('is_active', true)`
        // returns at most `db-max-rows` (1000) with a 200 and no error, so the
        // fold silently counted 1000 of 1767 active sounds; paging around that
        // was correct but shipped the whole active catalogue per call, and `/`
        // is force-dynamic. One row per category can never reach the cap.
        const { data, error } = await supabase.rpc('get_active_category_counts')

        if (error) {
          throw new SupabaseError(`Failed to fetch category counts: ${error.message}`, error)
        }

        const counts: Record<string, number> = {}
        for (const row of (data as { category: string; sound_count: number }[] | null) || []) {
          counts[row.category] = Number(row.sound_count)
        }

        // The aggregate counts flagged rows; take them back out so a category
        // chip never promises more sounds than its page lists.
        const { data: flagged, error: flaggedError } = await supabase
          .from('sounds')
          .select('category')
          .eq('is_active', true)
          .contains('tags', [MATURE_TAG])
        if (flaggedError) {
          throw new SupabaseError(`Failed to fetch flagged category counts: ${flaggedError.message}`, flaggedError)
        }
        for (const row of (flagged as { category: string }[] | null) || []) {
          if (counts[row.category]) counts[row.category] -= 1
        }
        return counts
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getCategoryCounts' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return {}
    }
  },

  // Get aggregated stats for hero section
  async getHeroStats(): Promise<{
    totalSounds: number
    totalCategories: number
    latestTeslaYear: number
    totalDownloads: number
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const [soundsResult, categoriesResult, downloadsResult] = await Promise.all([
        supabase.from('sounds').select('id', { count: 'exact', head: true }).eq('is_active', true).or(EXCLUDE_MATURE_FILTER),
        supabase.from('categories').select('name'),
        supabase.rpc('get_total_downloads')
      ])

      const totalSounds = soundsResult.count || 0
      const totalCategories = withoutRetiredCategories(categoriesResult.data ?? []).length
      const totalDownloads = (downloadsResult.data as number | null) || 0

      return {
        totalSounds,
        totalCategories,
        latestTeslaYear: 2025, // Latest compatible Tesla year
        totalDownloads
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getHeroStats' } })
      return {
        totalSounds: 0,
        totalCategories: 0,
        latestTeslaYear: 2025,
        totalDownloads: 0
      }
    }
  }
}
